<template>
    <Head :title="t('teamResults.title')" />
    <div class="flex flex-col h-full overflow-y-auto bg-surface-sunken">
        <div class="max-w-[860px] w-full mx-auto px-10 py-10 flex flex-col gap-6">
            <header>
                <div class="flex items-center gap-2 mb-2">
                    <BaseLink href="/reflections" class="body-small text-ink-muted hover:text-ink flex items-center gap-1">
                        <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("navigation.panes.reflections") }}
                    </BaseLink>
                </div>
                <BasePageTitle :title="t('teamResults.title')" />
                <p class="mt-2 body text-ink-muted">{{ t("teamResults.description") }}</p>
            </header>

            <div v-if="loading" class="body-small text-ink-muted">{{ t("teamResults.loading") }}</div>
            <BaseNotification v-else-if="loadError !== null" theme="error" :text="loadError || t('teamResults.loadError')" hide-dismiss />

            <BaseCard v-else-if="teams.length === 0" padding="lg" class="flex flex-col items-center text-center gap-3 py-10">
                <i class="bi bi-people text-4xl text-ink-muted" aria-hidden="true"></i>
                <BaseHeader size="s" class="text-ink">{{ t("teamResults.emptyTitle") }}</BaseHeader>
                <p class="body-small text-ink-muted max-w-md">
                    {{ t("teamResults.emptyDescription") }}
                </p>
            </BaseCard>

            <template v-else>
                <section v-for="team in teams" :key="team.team_id" class="flex flex-col gap-3">
                    <h2 class="body-small-bold uppercase tracking-wide text-ink-muted">{{ team.team_name || t("teamResults.unnamedTeam") }}</h2>
                    <div v-if="team.members.length === 0" class="rounded-xl border border-dashed border-border bg-surface-raised px-5 py-6 body-small text-ink-muted">
                        {{ t("teamResults.noMembers") }}
                    </div>
                    <BaseCard v-for="member in team.members" :key="member.user_uuid" padding="md" class="flex flex-col gap-3">
                        <div class="flex items-center justify-between gap-4">
                            <div class="min-w-0">
                                <div class="body-small-bold text-ink truncate">{{ member.name || member.email }}</div>
                                <div v-if="member.name" class="details text-ink-muted truncate">{{ member.email }}</div>
                            </div>
                        </div>
                        <div v-if="member.cycles.length === 0" class="body-small text-ink-muted">{{ t("teamResults.noAssessments") }}</div>
                        <div v-else class="flex flex-col gap-2">
                            <div v-for="cycle in member.cycles" :key="cycle.cycle_id" class="flex items-center justify-between gap-3 flex-wrap">
                                <div class="flex items-center gap-2 min-w-0">
                                    <span class="body-small text-ink truncate">{{ cycle.campaign_name || cycle.template_name }}</span>
                                    <StatusChip :state="cycleStateChipState(cycle.state)">{{ cycleStateLabel(cycle.state) }}</StatusChip>
                                </div>
                                <BaseLink
                                    v-if="cycle.report_available"
                                    :href="`/reflections/360/${cycle.cycle_id}/results`"
                                    :aria-label="t('teamResults.viewNamedReport', { name: cycle.campaign_name || cycle.template_name })"
                                    class="base-link body-small inline-flex items-center gap-1 shrink-0">
                                    {{ t("teamResults.viewReport") }}
                                    <i class="bi bi-arrow-right" aria-hidden="true"></i>
                                </BaseLink>
                                <span v-else class="details text-ink-muted shrink-0">{{ t("teamResults.noReport") }}</span>
                            </div>
                        </div>
                    </BaseCard>
                </section>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { TeamResults, TeamResultsResponse } from "~vue/api/generated/reflections";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseHeader from "~vue/components/BaseHeader.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { cycleStateChipState, cycleStateLabel } from "~vue/utils/threeSixty";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const teams = ref<TeamResults[]>([]);
const loading = ref(true);
const loadError = ref<string | null>(null);

async function reload() {
    const { data, error: fetchError } = await useFetch("/v1/reflections/team-results").json<TeamResultsResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "";
        return;
    }
    loadError.value = null;
    teams.value = data.value?.teams ?? [];
}

onMounted(reload);
</script>
